import type { ReactNode } from "react";
import type {
  FiberSelectedPayload,
  SelectedElementDesignation,
} from "schemas/generated/fileModifiers";

import React, { createContext } from "react";

import { useRequiredContext } from "replo-utils/react/context";
import { createStore, useStore } from "zustand";

export type DisplayMode = "interactive" | "select";

interface ModifiersStoreData {
  selectedElementDesignations: SelectedElementDesignation[] | null;
  classNames: string;
  displayMode: DisplayMode;
  actions: {
    setSelectedElementDesignations: (
      payload: FiberSelectedPayload | null,
    ) => void;
    setClassNames: (classNames: string) => void;
    setDisplayMode: (displayMode: DisplayMode) => void;
    toggleDisplayMode: () => void;
    resetToDefaults: () => void;
  };
}

const createModifiersStore = () => {
  return createStore<ModifiersStoreData>((set, get) => ({
    selectedElementDesignations: null,
    classNames: "",
    displayMode: "interactive",
    sectionClicked: false,
    actions: {
      setSelectedElementDesignations: (
        payload: FiberSelectedPayload | null,
      ) => {
        // When setting a selected element, also derive the related values
        if (payload) {
          set({
            selectedElementDesignations: payload.selectedElementDesignations,
          });
        } else {
          set({
            selectedElementDesignations: null,
            classNames: "",
          });
        }
      },
      setClassNames: (classNames: string) => set({ classNames }),
      setDisplayMode: (displayMode: DisplayMode) => set({ displayMode }),
      toggleDisplayMode: () => {
        const currentDisplayMode = get().displayMode;
        const newDisplayMode =
          currentDisplayMode === "interactive" ? "select" : "interactive";

        // NOTE (Kurt, 2025-07-16): Reset the store when toggling to interactive mode. This way we
        // have a fresh state when someone clicks on the select button again.
        if (newDisplayMode === "interactive") {
          get().actions.setSelectedElementDesignations(null);
          set({
            displayMode: newDisplayMode,
          });
        } else {
          set({ displayMode: newDisplayMode });
        }
      },
      resetToDefaults: () =>
        set({
          selectedElementDesignations: null,
          classNames: "",
          displayMode: "interactive",
        }),
    },
  }));
};

type ModifiersContextType = {
  store: ReturnType<typeof createModifiersStore>;
};

const ModifiersContext = createContext<ModifiersContextType | null>(null);

export const useModifiersContext = () => {
  return useRequiredContext(ModifiersContext);
};

interface ModifiersProviderProps {
  children: ReactNode;
}

export function ModifiersProvider({ children }: ModifiersProviderProps) {
  const [store] = React.useState(() => createModifiersStore());

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  return (
    <ModifiersContext.Provider value={contextValue}>
      {children}
    </ModifiersContext.Provider>
  );
}

// NOTE (Kurt, 2025-07-16): Main hook to access the store
export function useModifiersStore<T = ModifiersStoreData>(
  selector?: (state: ModifiersStoreData) => T,
): T {
  const { store } = useModifiersContext();
  // @ts-expect-error - useStore just returns the entire state when no selector is
  // provided, but apparently zustand's types don't understand this
  return useStore(store, selector);
}

// NOTE (Kurt, 2025-07-16): Convenience hooks for accessing specific values from the store.
// Define more as needed.
export const useSeletedElementDesignations = () =>
  useModifiersStore((state) => state.selectedElementDesignations);

export const useDefaultSelectedElementDesignation = () =>
  useModifiersStore((state) =>
    state.selectedElementDesignations?.find(
      (designation) => designation.type === "default",
    ),
  );

export const useIsAnyElementSelected = () =>
  useModifiersStore((state) => Boolean(state.selectedElementDesignations));

export const useModifiersStoreActions = () =>
  useModifiersStore((state) => state.actions);
